import type { SavedTargetingRules } from 'definitions';

/**
 * Identity, diff and fingerprint for an experiment's targeting, shared by the dashboard and the
 * API so "edited since load" means the same thing on both sides.
 *
 * A condition is one chip in the Target Area. Its key ignores ids, timestamps and list order, so
 * two targetings with the same conditions in a different order are the same targeting.
 */

export type TargetingGroup = 'device' | 'url' | 'cookie' | 'activation';

export type TargetingCondition = { group: TargetingGroup; key: string; label: string };

export type TargetingDiff = { adds: TargetingCondition[]; removes: TargetingCondition[]; keeps: TargetingCondition[] };

/** The order groups are listed in everywhere: summaries, chips and diffs. */
export const TARGETING_GROUP_ORDER: readonly TargetingGroup[] = ['device', 'url', 'cookie', 'activation'];

export const EMPTY_TARGETING: SavedTargetingRules = { url: [], cookie: [], device: [], activation: [] };

type ExperimentTargetingRows = {
  urlTargeting?: readonly { url: string; condition: string | null }[] | null;
  cookieTargeting?: readonly { cookieName: string | null; cookieValues: unknown }[] | null;
  deviceTargeting?: readonly { device: string | null }[] | null;
  activationTargeting?: readonly { kind: string; value: string }[] | null;
};

/**
 * An experiment's four rule tables in the saved-rules shape, ids and timestamps dropped. Not
 * validated: the server runs the result through validateSavedRules, and the dashboard only
 * compares and displays it.
 */
export function rulesFromExperiment(experiment: ExperimentTargetingRows): SavedTargetingRules {
  return {
    url: (experiment.urlTargeting ?? []).map(({ url, condition }) => ({
      url,
      condition: condition as SavedTargetingRules['url'][number]['condition'],
    })),
    cookie: (experiment.cookieTargeting ?? []).map(({ cookieName, cookieValues }) => ({
      cookieName: cookieName ?? '',
      cookieValues: Array.isArray(cookieValues) ? cookieValues.filter((v): v is string => typeof v === 'string') : [],
    })),
    device: (experiment.deviceTargeting ?? []).map(({ device }) => ({
      device: device as SavedTargetingRules['device'][number]['device'],
    })),
    activation: (experiment.activationTargeting ?? []).map(({ kind, value }) => ({
      kind: kind as SavedTargetingRules['activation'][number]['kind'],
      value,
    })),
  };
}

export const isEmptyTargeting = (rules: SavedTargetingRules): boolean =>
  TARGETING_GROUP_ORDER.every((group) => rules[group].length === 0);

/**
 * Every rule as a condition, in group order. Values are trimmed; only device names are
 * lowercased for the key, since URL and cookie values match case-sensitively at runtime.
 */
export function flattenTargeting(rules: SavedTargetingRules): TargetingCondition[] {
  const device = rules.device.map(({ device }) => ({
    group: 'device' as const,
    key: `device:${String(device).trim().toLowerCase()}`,
    label: String(device),
  }));
  const url = rules.url.map(({ url, condition }) => ({
    group: 'url' as const,
    key: `url:${condition}:${url.trim()}`,
    label: `URL ${condition} ${url.trim()}`,
  }));
  const cookie = rules.cookie.map(({ cookieName, cookieValues }) => {
    const values = cookieValues.map((value) => value.trim()).sort();
    const name = cookieName.trim();
    return {
      group: 'cookie' as const,
      key: `cookie:${name}:${values.join('\u0000')}`,
      label: `Cookie ${name ? `${name} ` : ''}contains ${values.join(' or ')}`,
    };
  });
  const activation = rules.activation.map(({ kind, value }) => ({
    group: 'activation' as const,
    key: `activation:${kind}:${value.trim()}`,
    label: kind === 'event' ? `Event ${value.trim()}` : `Element ${value.trim()} exists`,
  }));
  return [...device, ...url, ...cookie, ...activation];
}

/** What changes going from `current` to `next`. A condition in both is a keep. */
export function diffTargeting(current: SavedTargetingRules, next: SavedTargetingRules): TargetingDiff {
  const currentKeys = new Set(flattenTargeting(current).map(({ key }) => key));
  const nextConditions = flattenTargeting(next);
  const nextKeys = new Set(nextConditions.map(({ key }) => key));
  return {
    adds: nextConditions.filter(({ key }) => !currentKeys.has(key)),
    removes: flattenTargeting(current).filter(({ key }) => !nextKeys.has(key)),
    keeps: nextConditions.filter(({ key }) => currentKeys.has(key)),
  };
}

export const isSameTargeting = (a: SavedTargetingRules, b: SavedTargetingRules): boolean =>
  targetingHash(a) === targetingHash(b);

/**
 * A short, order-independent fingerprint of a targeting: FNV-1a over the sorted condition keys.
 * Stored on the experiment when a saved targeting is loaded, then compared with the live rules
 * to tell whether they were edited since. Synchronous, so the dashboard can recompute it on
 * every render; collisions only ever mean an edit goes unflagged.
 */
export function targetingHash(rules: SavedTargetingRules): string {
  const text = [...new Set(flattenTargeting(rules).map(({ key }) => key))].sort().join('\n');
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}
